import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { SnagStatusBadge, SnagPriorityBadge } from './SnagStatusBadge';

import EmptyState from '../../../components/ui/EmptyState';
import Skeleton from '../../../components/ui/Skeleton';
import { useLanguage } from '../../../contexts/LanguageContext';

const SnagTable = ({ snags, loading, onStatusChange, onDelete, onRestore }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  if (loading) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t('snag')}</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground hidden md:table-cell">{t('site')}</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t('status')}</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground hidden sm:table-cell">{t('priority')}</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground hidden lg:table-cell">{t('assigned')}</th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground hidden lg:table-cell">{t('date')}</th>
              <th className="text-right py-3 px-4 font-medium text-muted-foreground">{t('actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            <Skeleton variant="table-row" count={5} cols={7} />
          </tbody>
        </table>
      </div>
    );
  }

  if (!snags?.length) {
    return (
      <EmptyState
        icon="ClipboardCheck"
        title={t('noSnagsFound')}
        description={t('noSnagsFoundDesc')}
        className="py-10"
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t('snag')}</th>
            <th className="text-left py-3 px-4 font-medium text-muted-foreground hidden md:table-cell">{t('site')}</th>
            <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t('status')}</th>
            <th className="text-left py-3 px-4 font-medium text-muted-foreground hidden sm:table-cell">{t('priority')}</th>
            <th className="text-left py-3 px-4 font-medium text-muted-foreground hidden lg:table-cell">{t('assigned')}</th>
            <th className="text-left py-3 px-4 font-medium text-muted-foreground hidden lg:table-cell">{t('date')}</th>
            <th className="text-right py-3 px-4 font-medium text-muted-foreground">{t('actions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {snags?.map(snag => (
            <tr key={snag?.id} className="hover:bg-muted/30 transition-colors">
              <td className="py-3 px-4">
                <div>
                  <p className="font-medium text-foreground line-clamp-1">{snag?.title}</p>
                  {snag?.location && (
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                      <Icon name="MapPin" size={10} />
                      {snag?.location}
                    </p>
                  )}
                  {snag?.photo_paths?.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                      <Icon name="Image" size={10} />
                      {snag?.photo_paths?.length} {snag?.photo_paths?.length > 1 ? t('photos') : t('photo')}
                    </p>
                  )}
                </div>
              </td>
              <td className="py-3 px-4 hidden md:table-cell">
                <span className="text-muted-foreground">{snag?.sites?.site_name || '—'}</span>
              </td>
              <td className="py-3 px-4">
                <SnagStatusBadge status={snag?.status} />
              </td>
              <td className="py-3 px-4 hidden sm:table-cell">
                <SnagPriorityBadge priority={snag?.priority} />
              </td>
              <td className="py-3 px-4 hidden lg:table-cell">
                <span className="text-muted-foreground text-xs">
                  {snag?.assigned_user?.full_name || t('unassigned')}
                </span>
              </td>
              <td className="py-3 px-4 hidden lg:table-cell">
                <span className="text-muted-foreground text-xs">
                  {new Date(snag?.created_at)?.toLocaleDateString()}
                </span>
              </td>
              <td className="py-3 px-4 text-right">
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Eye"
                    onClick={() => navigate(`/snags/${snag?.id}`)}
                  >
                    {t('view')}
                  </Button>
                  {/* NSherjFh PR 4: admin Delete (active view) or Restore
                      (deleted view), mutually exclusive at the page level. */}
                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => onDelete(snag)}
                      aria-label={`Delete ${snag?.title}`}
                      className="text-destructive hover:text-destructive/80"
                    >
                      Delete
                    </Button>
                  )}
                  {onRestore && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="RotateCcw"
                      onClick={() => onRestore(snag)}
                      aria-label={`Restore ${snag?.title}`}
                      className="text-accent hover:text-accent/80"
                    >
                      Restore
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SnagTable;
